/* eslint-disable import/no-unresolved */
import axios from 'axios';
import SecureStorage from 'secure-web-storage';
import CryptoJS, { AES, SHA256 } from 'crypto-js';

import { isAuthenticatedVar, isLoggedInVar } from 'Apollo/config';
import { getQueryParams } from 'utils';

const setIsAuthenticatedVar = accessToken => {
    if (accessToken) {
        isAuthenticatedVar(true);
        isLoggedInVar(true);
    }
};

export const secureStorage = new SecureStorage(sessionStorage, {
    hash: key => SHA256(key, process.env.REACT_APP_SECRET_KEY).toString(),
    encrypt: data => AES.encrypt(data, process.env.REACT_APP_SECRET_KEY).toString(),
    decrypt: data => AES.decrypt(data, process.env.REACT_APP_SECRET_KEY).toString(CryptoJS.enc.Utf8),
});

export const handleAuthData = async () => {
    try {
        const queryParams = getQueryParams();

        if (!queryParams.code) {
            console.log('handleAuthData: No auth code in URL');
            return false;
        }

        // Clear previous session
        [
            'access_token', 'id_token', 'refresh_token', 'expires_at',
            'profile', 'selectedTenantName', 'selectedTenant', 'role', 'allowedModule',
        ].forEach(el => {
            try { secureStorage.removeItem(el); } catch (e) { /* ignore */ }
        });

        const body = {
            code: queryParams.code,
            state: queryParams.state,
        };

        const url = `${process.env.REACT_APP_BASE_LINK}api/callback`;

        const response = await axios.post(url, body, {
            headers: {
                'Content-Type': 'application/json',
                'x-isRestAPI': true,
            },
        });

        const data = response.data?.data || response.data;

        if (!data) {
            console.error('handleAuthData: Response data is empty');
            return false;
        }


        // ========== DECODE ID TOKEN ==========
        if (data.id_token) {
            try {
                const payload = JSON.parse(atob(data.id_token.split('.')[1]));
            } catch (e) {
                console.log('Could not decode id_token:', e.message);
            }
        }

        // ========== DECODE ACCESS TOKEN ==========
        if (data.access_token) {
            try {
                const payload = JSON.parse(atob(data.access_token.split('.')[1]));
            } catch (e) {
                console.log('Access token is opaque (not JWT), skipping decode');
            }
        }

        if (!data.access_token) {
            console.error('handleAuthData: No access_token in response');
            return false;
        }

        secureStorage.setItem('access_token', data.access_token);

        if (data.id_token) {
            secureStorage.setItem('id_token', data.id_token);
        }

        if (data.refresh_token) {
            secureStorage.setItem('refresh_token', data.refresh_token);
        }

        if (data.expires_in) {
            const expiresAt = new Date(
                Date.now() + data.expires_in * 1000
            ).toISOString();
            secureStorage.setItem('expires_at', expiresAt);
        }

        const storedToken = secureStorage.getItem('access_token');
        const storedIdToken = secureStorage.getItem('id_token');

        if (!storedToken) {
            console.error('handleAuthData: Token storage verification failed');
            return false;
        }

        // Clear code from URL to prevent reuse
        window.history.replaceState({}, document.title, window.location.pathname);

        setIsAuthenticatedVar(data.access_token);
        return true;
    } catch (error) {
        console.error(
            'handleAuthData: ❌ Error:',
            error.response?.status,
            error.response?.data || error.message
        );
        return false;
    }
};

export const signOutUser = async () => {

    const idToken = secureStorage.getItem('id_token');

    [
        'access_token', 'id_token', 'refresh_token', 'expires_at',
        'profile', 'selectedTenantName', 'selectedTenant', 'role', 'allowedModule',
    ].forEach(el => {
        try { secureStorage.removeItem(el); } catch (e) { /* ignore */ }
    });
    secureStorage.clear();
    sessionStorage.clear();

    // ✅ HARDCODED to avoid .env issues
    const tenantId = 'b9ad39bb-d4e9-4878-ae67-b3a5f56a5906';
    const logoutBase = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`;
    const postLogoutRedirect = encodeURIComponent('http://localhost:4200/login');

    let logoutUrl = `${logoutBase}?post_logout_redirect_uri=${postLogoutRedirect}`;

    if (idToken) {
        logoutUrl += `&id_token_hint=${idToken}`;
    }
    window.location.href = logoutUrl;
};

/* Setters */
export const setProfile = data => secureStorage.setItem('profile', data);
export const setSelectedTenant = data => secureStorage.setItem('selectedTenant', data);
export const setSelectedTenantName = data => secureStorage.setItem('selectedTenantName', data);
export const setRole = data => secureStorage.setItem('role', data);
export const setAllowedModule = data => secureStorage.setItem('allowedModule', data);

/* function to fetch id_token from session storage */
export const getAuthorizationHeaderValue = () => secureStorage.getItem('id_token');

/* function to fetch access_token from session storage */
export const getAccessToken = () => secureStorage.getItem('access_token');

/* function to check authenticity of the user */
export const isAuthenticated = () => {
    const expiresAt = secureStorage.getItem('expires_at');
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() > Date.now();
};

/* Getters */
export const getUserRole = () => secureStorage.getItem('role');
export const getProfile = () => secureStorage.getItem('profile');
export const getSelectedTenant = () => secureStorage.getItem('selectedTenant');
export const getSelectedTenantName = () => secureStorage.getItem('selectedTenantName');
export const getAllowedModule = () => secureStorage.getItem('allowedModule');